import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send } from "lucide-react";

const fmtTime = (iso) => new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

export default function Messages() {
  const { userId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [convs, setConvs] = useState([]);
  const [thread, setThread] = useState([]);
  const [otherName, setOtherName] = useState("");
  const [text, setText] = useState("");
  const bottomRef = useRef(null);

  const loadConvs = useCallback(() => api.get("/conversations").then((r) => setConvs(r.data)), []);

  useEffect(() => { loadConvs(); }, [loadConvs]);

  useEffect(() => {
    if (!userId) { setThread([]); return; }
    const loadThread = () => api.get(`/messages/${userId}`).then((r) => setThread(r.data));
    loadThread();
    // Resolve display name — try professional first, fallback to conversation list
    api.get(`/professionals/${userId}`)
      .then((r) => setOtherName(r.data?.name))
      .catch(() => {
        const c = convs.find((x) => x.user_id === userId);
        if (c) setOtherName(c.name);
      });
    const iv = setInterval(loadThread, 5000);
    return () => clearInterval(iv);
  }, [userId, convs]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread]);

  const send = async () => {
    if (!text.trim() || !userId) return;
    await api.post("/messages", { to_user_id: userId, content: text.trim() });
    setText("");
    api.get(`/messages/${userId}`).then((r) => setThread(r.data));
    loadConvs();
  };

  return (
    <div className="container-optia py-10 animate-fade-in">
      <h1 className="font-display text-2xl font-semibold text-navy-900">Mensagens</h1>
      <div className="grid md:grid-cols-[300px_1fr] gap-5 mt-6">
        <Card className="card-soft h-fit">
          <CardContent className="p-3">
            {convs.length === 0 && <p className="text-sm text-slate-500 p-4">Nenhuma conversa ainda.</p>}
            <div className="space-y-1" data-testid="conv-list">
              {convs.map((c) => (
                <button key={c.user_id} onClick={()=>nav(`/mensagens/${c.user_id}`)}
                  className={`w-full text-left p-3 rounded-xl transition ${userId===c.user_id ? "bg-teal-50 border border-teal-200" : "hover:bg-slate-50"}`}
                  data-testid={`conv-${c.user_id}`}>
                  <div className="font-medium text-navy-900 text-sm">{c.name}</div>
                  <div className="text-xs text-slate-500 truncate">{c.last_message}</div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="card-soft">
          <CardContent className="p-0 flex flex-col h-[560px]">
            {!userId ? (
              <div className="flex-1 grid place-items-center text-slate-500">Selecione uma conversa</div>
            ) : (
              <>
                <div className="px-5 py-4 border-b border-slate-200 font-medium text-navy-900">{otherName || "Conversa"}</div>
                <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="msg-thread">
                  {thread.map((m) => (
                    <div key={m.id} className={`flex ${m.from_user_id === user.id ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] px-4 py-2 rounded-2xl text-[15px] ${m.from_user_id === user.id ? "bg-teal-500 text-white rounded-br-md" : "bg-slate-100 text-slate-800 rounded-bl-md"}`}>
                        {m.content}
                        <div className={`text-[10px] mt-1 ${m.from_user_id === user.id ? "text-white/70" : "text-slate-500"}`}>{fmtTime(m.created_at)}</div>
                      </div>
                    </div>
                  ))}
                  <div ref={bottomRef} />
                </div>
                <div className="p-3 border-t border-slate-200 flex gap-2">
                  <Input value={text} onChange={(e)=>setText(e.target.value)} onKeyDown={(e)=>e.key==="Enter"&&send()} placeholder="Escreva…" data-testid="msg-input" />
                  <Button onClick={send} className="bg-teal-500 hover:bg-teal-600" data-testid="msg-send"><Send className="w-4 h-4" /></Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
